import { computed, watch } from 'vue'
import { useBasketState } from '../useBasketState'
import {
  calculateTotalPrice,
  createPriceBreakdown,
  type PriceCalculation,
} from '../../lib/calculationUtils'
import { useInstitutionStore } from '@entities/institution'

/**
 * ✅ ОТДЕЛЬНЫЙ COMPOSABLE для расчета итоговых цен
 *
 * Отвечает ТОЛЬКО за:
 * - Расчет финальной суммы к оплате
 * - Расчет сервисного сбора
 * - Создание детализации цен для UI
 *
 * ✅ ЕДИНАЯ точка расчета - все вычисления в одном месте
 */
export function usePriceCalculation() {
  const basketState = useBasketState()
  const institution = useInstitutionStore()

  /**
   * Процент сервисного сбора
   */
  const serviceFeePercent = computed(() => {
    const inst = institution.institution as any
    return Number(inst?.service_fee_percent) || 0
  })

  /**
   * ✅ ГЛАВНЫЙ РАСЧЕТ: итоговые цены с учетом ВСЕХ скидок и доплат
   *
   * Порядок расчёта:
   * 1. Сумма корзины
   * 2. Минус промокод
   * 3. Минус баллы
   * 4. Минус подарок за сумму (он лежит в корзине по полной цене)
   * 5. Сервисный сбор = (сумма после скидок + доставка) × percent / 100
   *    ↑ доставка ВКЛЮЧЕНА в базу сервисного сбора
   * 6. Плюс доставка
   * = ФИНАЛЬНАЯ СУММА К ОПЛАТЕ
   */
  const priceCalculation = computed((): PriceCalculation => {
    const pointsDiscount = basketState.useLoyaltyPoints ? basketState.pointsToUse : 0

    return calculateTotalPrice(
      basketState.cartTotal,           // Исходная сумма корзины
      basketState.promocodeDiscount,   // Скидка по промокоду
      pointsDiscount,                  // Скидка по баллам
      basketState.deliveryCost,        // Доставка
      serviceFeePercent.value,         // Сервисный сбор %
      // Подарок за сумму и подарок за частоту — разные механики, но в заказе подарок
      // один, поэтому в суммах это одна статья «скидка за подарок».
      basketState.giftTierDiscount + basketState.weeklyGiftDiscount,
    )
  })

  /**
   * Сервисный сбор.
   *
   * Берём из того же расчёта, что и итог: раньше он считался здесь вторым,
   * независимым выражением, и в крайних случаях (скидка больше стоимости товаров)
   * расходился с суммой, которую видит гость.
   */
  const serviceFee = computed(() => priceCalculation.value.serviceFee)

  /**
   * Детализация цен для отображения в UI
   */
  const priceBreakdown = computed(() => {
    return createPriceBreakdown(priceCalculation.value)
  })

  /**
   * Финальная сумма к оплате
   */
  const finalAmount = computed(() => {
    return priceCalculation.value.finalAmount
  })

  // ========================================
  // SIDE EFFECTS: Обновляем basketState
  // ========================================

  /**
   * Автоматически обновляем процент сервисного сбора в state
   */
  watch(serviceFeePercent, (percent) => {
    basketState.setServiceFeePercent(percent)
  }, { immediate: true })

  /**
   * Автоматически обновляем сумму сервисного сбора в state
   */
  watch(serviceFee, (fee) => {
    basketState.setServiceFee(fee)
  }, { immediate: true })

  /**
   * Автоматически обновляем финальную сумму в state
   */
  watch(finalAmount, (amount) => {
    basketState.setFinalAmount(amount)
  }, { immediate: true })

  return {
    // Computed
    serviceFeePercent,
    serviceFee,
    priceCalculation,
    priceBreakdown,
    finalAmount,
  }
}
